import { useCallback, useEffect } from "react";

import * as Sentry from "@sentry/react";
import { SpinnerCenter } from "@shapr3d/frontend-lib";

import ChargebeeCheckout from "../components/ChargebeeCheckout/ChargebeeCheckout";
import { CheckoutErrorModal } from "../components/ChargebeeCheckout/components/CreateCheckoutErrorModal/CreateCheckoutErrorModal";
import { PaddleBillingCheckout } from "../components/PaddleBillingCheckout/PaddleBillingCheckout";
import UnderMaintenanceOrChildren from "../components/UnderMaintenanceOrChildren/UnderMaintenanceOrChildren";
import { BrowserLocaleIntlProvider } from "../contexts/BrowserLocaleIntlProvider";
import useNavigation from "../hooks/navigation";
import useAccount, { paymentPlatformSentryTag } from "../hooks/useAccount";
import { useCreateCheckout, useInitialPeriod, useInitialTier } from "../hooks/useCheckout";
import { useTeam, useTeamSubscriptions } from "../hooks/useTeams";
import { SearchParam, SearchParamIntent, SubscriptionTier } from "../types";

export const CheckoutRouter = () => {
  const initialPeriod = useInitialPeriod();
  const initialTier = useInitialTier();
  const navigate = useNavigation();
  const { data: accountData, isError: isAccountError, error: accountError } = useAccount();
  const onErrorClose = useCallback(() => navigate.goToSubscription(), [navigate]);

  const primaryTeam =
    accountData?.teams.find(({ isPrimary }) => isPrimary) ?? accountData?.teams[0];

  const { isOwner, isSeatManager } = useTeam(primaryTeam?.id);
  const canFetchPrimaryTeamSubscriptions = Boolean(primaryTeam?.id) && (isOwner || isSeatManager);

  const {
    data: teamSubscriptionData,
    isLoading: isTeamSubscriptionsLoading,
    isError: isTeamSubscriptionsError,
    error: teamSubscriptionsError,
  } = useTeamSubscriptions(primaryTeam?.id);

  const awaitsStudioSubscriptionContext =
    initialTier === SubscriptionTier.Studio && canFetchPrimaryTeamSubscriptions;

  const isIndividualUpgradingToStudio =
    initialTier === SubscriptionTier.Studio &&
    teamSubscriptionData?.seatBasedTeamSubscription?.tier === SubscriptionTier.Individual;

  const canFetchCheckout =
    !isAccountError &&
    Boolean(accountData) &&
    Boolean(initialTier) &&
    !(awaitsStudioSubscriptionContext && isTeamSubscriptionsLoading) &&
    !(awaitsStudioSubscriptionContext && isTeamSubscriptionsError) &&
    !isIndividualUpgradingToStudio;

  const {
    data: createCheckoutData,
    isError: isCreateCheckoutError,
    error: createCheckoutError,
  } = useCreateCheckout({
    period: initialPeriod,
    tier: initialTier,
    enabled: canFetchCheckout,
  });

  useEffect(() => {
    if (!createCheckoutData) {
      return;
    }

    if (createCheckoutData.checkoutType === "paddle_one_time") {
      Sentry.setTag(paymentPlatformSentryTag, "paddle_billing");
    } else {
      Sentry.setTag(paymentPlatformSentryTag, "chargebee");
    }
  }, [createCheckoutData]);

  useEffect(() => {
    if (!isIndividualUpgradingToStudio || !primaryTeam) {
      return;
    }
    navigate.goToTeamSubscription({
      teamID: primaryTeam.id,
      additionalQueryParams: { [SearchParam.Intent]: SearchParamIntent.Upgrade },
    });
  }, [isIndividualUpgradingToStudio, primaryTeam, navigate]);

  if (isAccountError) {
    return (
      <CheckoutErrorModal
        open
        error={
          accountError instanceof Error
            ? accountError
            : new Error(String(accountError ?? "Unknown error"))
        }
        onClose={onErrorClose}
      />
    );
  }

  if (!accountData) {
    return <SpinnerCenter />;
  }

  if (initialTier === undefined) {
    return <SpinnerCenter />;
  }

  if (awaitsStudioSubscriptionContext && isTeamSubscriptionsLoading) {
    return <SpinnerCenter />;
  }

  if (awaitsStudioSubscriptionContext && isTeamSubscriptionsError) {
    return (
      <CheckoutErrorModal
        open
        error={
          teamSubscriptionsError instanceof Error
            ? teamSubscriptionsError
            : new Error(String(teamSubscriptionsError ?? "Unknown error"))
        }
        onClose={onErrorClose}
      />
    );
  }

  if (isIndividualUpgradingToStudio) {
    return <SpinnerCenter />;
  }

  if (canFetchCheckout && !createCheckoutData && !isCreateCheckoutError) {
    return <SpinnerCenter />;
  }

  if (isCreateCheckoutError) {
    return <CheckoutErrorModal open error={createCheckoutError} onClose={onErrorClose} />;
  }

  if (!createCheckoutData) {
    return <SpinnerCenter />;
  }

  if (createCheckoutData.checkoutType === "paddle_one_time") {
    return (
      <UnderMaintenanceOrChildren>
        <PaddleBillingCheckout
          prices={createCheckoutData.prices}
          customerId={createCheckoutData.paddleCustomerId}
        />
      </UnderMaintenanceOrChildren>
    );
  }

  return (
    <BrowserLocaleIntlProvider>
      <UnderMaintenanceOrChildren>
        <ChargebeeCheckout initialCheckoutData={createCheckoutData} />
      </UnderMaintenanceOrChildren>
    </BrowserLocaleIntlProvider>
  );
};
